package com.advaita.cloud.drive.shared

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable

/**
 * The resolved authenticated user. `username` is az2's opaque user id (the token's
 * `user_name` claim — NOT a human-readable login); drive uses it as the `ownerId`
 * for tenant fencing and as the `<ownerId>` segment of the object_key prefix. `email`
 * comes from /users/info; `orgId` from the token (email-domain fallback via
 * DomainOrgCache). username/email/orgId are non-null at the point Spring Security
 * puts this on the authentication principal.
 *
 * See spec §7.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
data class UserInfoDto(
    val username: String,
    val email: String,
    val orgId: String,
    val displayName: String? = null,  // backward-compat for the frontend; not used by tenant fencing
    val teamId: String? = null,
    // az2 role (from /users/info `currentRole`: ADMIN | USER | BASESPACE | …). Used only
    // to gate the ops/admin file-download endpoint; NOT used for tenant fencing.
    val role: String? = null,
    /**
     * ADV-1329 decision 20: where [orgId] came from — [ORG_CLAIM] (the token's `org_id`) or
     * [DOMAIN_FALLBACK] (DataHub's email-domain mapping). Null for a principal no resolver built.
     */
    val orgSource: String? = null,
    /**
     * ADV-1329 decision 20: on the domain fallback, whether this user SHOULD have carried an
     * `org_id` claim — true for an az2 ACTIVE user, false for a PENDING_APPROVAL one (whose
     * `/users/info` currentRole is `ACTIVATION`). Only meaningful with [DOMAIN_FALLBACK].
     */
    val tenancyExpected: Boolean = false,
    /**
     * ADV-1330 D6 (ruling S9): whether the share UI is on in this environment. Only `/api/me`
     * sets it, and it is false on every other principal. False means the frontend shows the
     * first S8 cap, "Sharing is not on yet in this environment.", and calls no share route.
     */
    val sharingEnabled: Boolean = false,
    /**
     * ADV-1330 D7: the az2 `org_shareable` claim. `OrgIdResolver` sets it on the [ORG_CLAIM]
     * path only, and only from a JSON boolean. Null means absent: an az2 that predates #35, a
     * value that is not a boolean, or a session with no `org_id`. Absent fails closed.
     */
    val orgShareable: Boolean? = null,
    /**
     * ADV-1330 D7 (ruling 2026-09-28, "D7 absent claim"; S8 row 9): whether this session can
     * create or widen a share. Only `/api/me` sets it, like [sharingEnabled], and it is false on
     * every other principal. It is true only for [ORG_CLAIM] with [orgShareable] == true.
     * `@EncodeDefault` puts `false` on the wire too: kotlinx omits a default value, and a reader
     * must never take a missing key for "allowed".
     */
    @EncodeDefault(EncodeDefault.Mode.ALWAYS)
    val sharingAllowed: Boolean = false,
    /**
     * ADV-1326c: the person's standing in DataHub, from UserVault's answer to the sign-in report.
     * Only `/api/me` sets it, and only while the check plane is on. Null means "no standing
     * known": the relay has not heard back yet (the first page load), the vault did not answer,
     * the vault predates ADV-1326b, or the checks are off. The frontend keeps its behaviour from
     * before admission for null, so null never holds anybody out. `@EncodeDefault` puts the null
     * on the wire too, so a reader sees "not known" rather than a missing key.
     */
    @EncodeDefault(EncodeDefault.Mode.ALWAYS)
    val admission: AdmissionDto? = null,
) {
    companion object {
        const val ORG_CLAIM = "ORG_CLAIM"
        const val DOMAIN_FALLBACK = "DOMAIN_FALLBACK"
    }
}
